// controllers/messageController.js (FRONTEND - Updated)
import apiService from '../services/apiService';
import Message from '../models/Message';
import Conversation from '../models/Conversation';

export const messageController = {
  // ==================== CONVERSATIONS ====================
  
  /**
   * Get all conversations for current user
   * @param {Object} filters - { filter: 'all|unread|groups', search: 'query' }
   * @returns {Promise<Object>} - { success, data: Conversation[], message }
   */
  getAllConversations: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams(filters).toString();
      const endpoint = queryParams ? `messages/conversations?${queryParams}` : 'messages/conversations';
      const response = await apiService.get(endpoint);
      
      const conversations = response.data?.map(item => Conversation.fromJSON(item)) || [];
      
      return { 
        success: true, 
        data: conversations, 
        total: conversations.length 
      };
    } catch (error) {
      console.error('Error fetching conversations:', error);
      return { 
        success: false, 
        data: [], 
        message: error.message || 'Failed to fetch conversations' 
      };
    }
  },

  /**
   * Create or get existing conversation
   * @param {Object} data - { participantId, participantIds, type: 'direct|group', name }
   * @returns {Promise<Object>} - { success, data: Conversation, message }
   */
  createConversation: async (data) => {
    try {
      const response = await apiService.post('messages/conversation', data);
      
      return { 
        success: true, 
        data: Conversation.fromJSON(response.data), 
        message: 'Conversation created successfully' 
      };
    } catch (error) {
      console.error('Error creating conversation:', error);
      return { 
        success: false, 
        data: null, 
        message: error.message || 'Failed to create conversation' 
      };
    }
  },

  // ==================== USERS ====================

  /**
   * Get all users for creating conversations
   * @param {Object} options - { search: 'query' }
   * @returns {Promise<Object>} - { success, data: User[], message }
   */
  getUsersList: async (options = {}) => {
    try {
      const { search } = options;
      const queryParams = search ? new URLSearchParams({ search }).toString() : '';
      const endpoint = queryParams ? `users?${queryParams}` : 'users';
      
      const response = await apiService.get(endpoint);
      
      // Handle nested response structure: response.data.users or response.users or response.data
      const users = response.data?.users || response.users || response.data || [];
      
      return { 
        success: true, 
        data: users,
        message: 'Users fetched successfully'
      };
    } catch (error) {
      console.error('Error fetching users:', error);
      return { 
        success: false, 
        data: [], 
        message: error.message || 'Failed to fetch users' 
      };
    }
  },

  /**
   * Search users
   * @param {string} query - Search query
   * @returns {Promise<Object>} - { success, data: User[], message }
   */
  searchUsers: async (query) => {
    try {
      const response = await apiService.get(`users/search?q=${encodeURIComponent(query)}`);
      
      return { 
        success: true, 
        data: response.data || []
      };
    } catch (error) {
      console.error('Error searching users:', error);
      return { 
        success: false, 
        data: [], 
        message: error.message || 'Failed to search users' 
      };
    }
  },

  // ==================== MESSAGES ====================

  /**
   * Get all messages in a conversation
   * @param {string} conversationId - Conversation ID
   * @param {Object} options - { page: 1, limit: 50 }
   * @returns {Promise<Object>} - { success, data: Message[], pagination, message }
   */
  getMessages: async (conversationId, options = {}) => {
    try {
      const { page = 1, limit = 50 } = options;
      const queryParams = new URLSearchParams({ page, limit }).toString();
      const endpoint = `messages/${conversationId}?${queryParams}`;
      
      const response = await apiService.get(endpoint);
      
      const messages = response.data?.map(item => Message.fromJSON(item)) || [];
      
      return { 
        success: true, 
        data: messages,
        pagination: response.pagination || {
          page,
          limit,
          total: messages.length,
          pages: Math.ceil(messages.length / limit)
        }
      };
    } catch (error) {
      console.error('Error fetching messages:', error);
      return { 
        success: false, 
        data: [], 
        message: error.message || 'Failed to fetch messages' 
      };
    }
  },

  /**
   * Send a text message
   * @param {Object} data - { conversationId, text, replyTo }
   * @returns {Promise<Object>} - { success, data: Message, message }
   */
  sendMessage: async (data) => {
    try {
      const message = new Message({
        ...data,
        type: 'text'
      });
      
      const validation = message.validate();
      if (!validation.isValid) {
        return { 
          success: false, 
          errors: validation.errors,
          message: validation.errors.join(', ')
        };
      }

      const response = await apiService.post('messages/send', message.toJSON());
      
      return { 
        success: true, 
        data: Message.fromJSON(response.data), 
        message: 'Message sent successfully' 
      };
    } catch (error) {
      console.error('Error sending message:', error);
      return { 
        success: false, 
        data: null, 
        message: error.message || 'Failed to send message' 
      };
    }
  },

  /**
   * Upload and send file message
   * @param {Object} data - { conversationId, file: File }
   * @returns {Promise<Object>} - { success, data: Message, message }
   */
  uploadFile: async (data) => {
    try {
      const { conversationId, file } = data;
      
      if (!conversationId) {
        return { success: false, message: 'Conversation ID is required' };
      }
      
      if (!file) {
        return { success: false, message: 'File is required' };
      }

      const formData = new FormData();
      formData.append('file', file);
      formData.append('conversationId', conversationId);

      const response = await apiService.postFormData('messages/upload', formData);
      
      return { 
        success: true, 
        data: Message.fromJSON(response.data), 
        message: 'File uploaded successfully' 
      };
    } catch (error) {
      console.error('Error uploading file:', error);
      return { 
        success: false, 
        data: null, 
        message: error.message || 'Failed to upload file' 
      };
    }
  },

  /**
   * Mark all messages in conversation as read
   * @param {string} conversationId - Conversation ID
   * @returns {Promise<Object>} - { success, message }
   */
  markAsRead: async (conversationId) => {
    try {
      await apiService.patch(`messages/${conversationId}/read`);
      
      return { 
        success: true, 
        message: 'Messages marked as read' 
      };
    } catch (error) {
      console.error('Error marking messages as read:', error);
      return { 
        success: false, 
        message: error.message || 'Failed to mark messages as read' 
      };
    }
  },

  /**
   * Delete a message
   * @param {string} messageId - Message ID
   * @returns {Promise<Object>} - { success, message }
   */
  deleteMessage: async (messageId) => {
    try {
      await apiService.delete(`messages/${messageId}`);
      
      return { 
        success: true, 
        message: 'Message deleted successfully' 
      };
    } catch (error) {
      console.error('Error deleting message:', error);
      return { 
        success: false, 
        message: error.message || 'Failed to delete message' 
      };
    }
  },

  // ==================== SEARCH & FILTER ====================

  /**
   * Search messages across all conversations
   * @param {string} query - Search query
   * @returns {Promise<Object>} - { success, data: Message[], message }
   */
  searchMessages: async (query) => {
    try {
      const response = await apiService.get(`messages/search?q=${encodeURIComponent(query)}`);
      
      const messages = response.data?.map(item => Message.fromJSON(item)) || [];
      
      return { 
        success: true, 
        data: messages 
      };
    } catch (error) {
      console.error('Error searching messages:', error);
      return { 
        success: false, 
        data: [], 
        message: error.message || 'Failed to search messages' 
      };
    }
  },

  /**
   * Get unread messages count
   * @returns {Promise<Object>} - { success, data: { count }, message }
   */
  getUnreadCount: async () => {
    try {
      const response = await apiService.get('messages/unread-count');
      
      return { 
        success: true, 
        data: { count: response.data?.count || 0 } 
      };
    } catch (error) {
      console.error('Error getting unread count:', error);
      return { 
        success: false, 
        data: { count: 0 }, 
        message: error.message 
      };
    }
  },

  // ==================== WEBHOOKS ====================

  /**
   * Trigger webhook for message events
   * @param {string} event - Event name (message.sent, message.read, etc.)
   * @param {Object} data - Event data
   * @returns {Promise<Object>} - { success, message }
   */
  triggerWebhook: async (event, data) => {
    try {
      const response = await apiService.post('webhooks/trigger', {
        event,
        data
      });
      
      return { 
        success: true, 
        data: response.data,
        message: 'Webhook triggered successfully' 
      };
    } catch (error) {
      console.error('Error triggering webhook:', error);
      return { 
        success: false, 
        message: error.message || 'Failed to trigger webhook' 
      };
    }
  },

  /**
   * Register webhook for message events
   * @param {Object} webhookData - { name, url, events: [] }
   * @returns {Promise<Object>} - { success, data, message }
   */
  registerWebhook: async (webhookData) => {
    try {
      const response = await apiService.post('webhooks', webhookData);
      
      return { 
        success: true, 
        data: response.data,
        message: 'Webhook registered successfully' 
      };
    } catch (error) {
      console.error('Error registering webhook:', error);
      return { 
        success: false, 
        message: error.message || 'Failed to register webhook' 
      };
    }
  },

  // ==================== REAL-TIME UPDATES ====================

  /**
   * Poll for new messages (for non-WebSocket setups)
   * @param {string} conversationId - Conversation ID
   * @param {Date} lastMessageTime - Last message timestamp
   * @returns {Promise<Object>} - { success, data: Message[], message }
   */
  pollNewMessages: async (conversationId, lastMessageTime) => {
    try {
      const timestamp = lastMessageTime ? new Date(lastMessageTime).toISOString() : null;
      const endpoint = timestamp 
        ? `messages/${conversationId}/new?since=${timestamp}`
        : `messages/${conversationId}/new`;
      
      const response = await apiService.get(endpoint);
      
      const messages = response.data?.map(item => Message.fromJSON(item)) || [];
      
      return { 
        success: true, 
        data: messages 
      };
    } catch (error) {
      console.error('Error polling new messages:', error);
      return { 
        success: false, 
        data: [], 
        message: error.message 
      };
    }
  },

  // ==================== TYPING INDICATORS ====================

  /**
   * Send typing indicator
   * @param {string} conversationId - Conversation ID
   * @param {boolean} isTyping - Typing status
   * @returns {Promise<Object>} - { success, message }
   */
  sendTypingIndicator: async (conversationId, isTyping = true) => {
    try {
      await apiService.post(`messages/${conversationId}/typing`, { isTyping });
      
      return { success: true };
    } catch (error) {
      console.error('Error sending typing indicator:', error);
      return { success: false, message: error.message };
    }
  },

  // ==================== UTILITIES ====================

  /**
   * Format conversations list for UI
   * @param {Conversation[]} conversations - Array of conversations
   * @param {string} currentUserId - Current user ID
   * @returns {Array} - Formatted conversations
   */
  formatConversationsForUI: (conversations, currentUserId) => {
    return conversations.map(conv => ({
      ...conv,
      formattedTime: conv.getFormattedTime(),
      displayName: conv.getDisplayName(),
      previewText: conv.getPreviewText(),
      hasUnread: conv.hasUnread(),
      isGroup: conv.isGroup()
    }));
  },

  /**
   * Format messages list for UI
   * @param {Message[]} messages - Array of messages
   * @param {string} currentUserId - Current user ID
   * @returns {Array} - Formatted messages
   */
  formatMessagesForUI: (messages, currentUserId) => {
    return messages.map(msg => ({
      ...msg,
      isMine: msg.isMine(currentUserId),
      formattedTime: msg.getFormattedTime(),
      relativeTime: msg.getRelativeTime(),
      isImage: msg.isImage(),
      isVideo: msg.isVideo(),
      fileSize: msg.getFileSize()
    }));
  },

  /**
   * Group messages by date
   * @param {Message[]} messages - Array of messages
   * @returns {Object} - Messages grouped by date
   */
  groupMessagesByDate: (messages) => {
    const grouped = {};
    
    messages.forEach(msg => {
      const date = new Date(msg.timestamp).toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric'
      });
      
      if (!grouped[date]) {
        grouped[date] = [];
      }
      
      grouped[date].push(msg);
    });
    
    return grouped;
  }
};

export default messageController;